import React, { useEffect, useState } from "react";
import { Button, Spin } from "antd";
import { AlertTriangle } from "lucide-react";
import ResultsStep from "../../rct-experiment/components/ResultsStep";
import { DEFAULT_SETUP_STATE, SetupState, SplitOption } from "../../rct-experiment/components/mockData";
import { fetchDesignResult, fetchRctExperimentConfig, DesignResult } from "../../rct-experiment/components/rctExperimentApi";

interface StepPowerAnalysisProps {
  jobConfigId: string;
  onNext: () => void;
  onPrevious: () => void;
}

// This step is the real RCT Experiment "Results" page, reused verbatim - same
// Compare Splits table, per-split tabs, market-level investment table, and
// actual-vs-predicted chart as the standalone RCT Experiment feature (see
// causal-loop/components/StepPowerAnalysis.tsx for the identical philosophy
// applied to Synthetic Control's ViewTestAndControlMarket). By the time this
// step is reached, StepProcessing has already polled the JobRun to
// SUCCESSFUL, and process_causal_loop_rct_job only returns after both engines
// finish - so design_result.json is already written; this only needs one
// fetch, not the standalone feature's own polling loop.
//
// jobConfigId is the only thing this needs from the wizard - the study name/
// durations/spend figures it renders come from re-fetching the saved
// JobConfig (fetchRctExperimentConfig, generic by jobConfigId - not tied to
// JobType), which also makes "View" from the job list work with no extra
// wiring in CreateCausalLoopRct.tsx.
const StepPowerAnalysis: React.FC<StepPowerAnalysisProps> = ({ jobConfigId, onNext, onPrevious }) => {
  const [setup, setSetup] = useState<SetupState>(DEFAULT_SETUP_STATE);
  const [designResult, setDesignResult] = useState<DesignResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [config, result] = await Promise.all([
          fetchRctExperimentConfig(jobConfigId),
          fetchDesignResult(jobConfigId),
        ]);
        if (cancelled) return;
        if (config) {
          setSetup((prev) => ({
            ...prev,
            studyName: config.name,
            uploaded: true,
            uploadStatus: "done",
            durations: config.durations,
            splits: (config.splits.length > 0 ? config.splits : ["5050"]) as SplitOption[],
            totalWeeklySpend: config.totalWeeklySpend,
            weeklyCap: config.weeklyCap,
          }));
        }
        if (!result) {
          setRunError(
            "No RCT 50/50 results were found for this study - the design engine may have failed while Bid Optimizer succeeded. You can still continue to Apply Bid for Bid Optimizer results."
          );
        } else {
          setDesignResult(result);
        }
      } catch (err) {
        console.error("Error loading Causal Loop (RCT) power analysis results:", err);
        if (!cancelled) {
          setRunError("Failed to load results for this study. Please try refreshing.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobConfigId]);

  if (loading) {
    return (
      <div className="text-center py-24">
        <Spin size="large" tip="Loading RCT 50/50 results..." />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {runError && !designResult ? (
        <div className="bg-white border border-red-200 rounded-xl p-6 shadow-sm mb-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-gray-900 mb-1">RCT design results unavailable</div>
              <p className="text-sm text-gray-600">{runError}</p>
            </div>
          </div>
        </div>
      ) : (
        <ResultsStep
          jobConfigId={jobConfigId}
          setup={setup}
          designResult={designResult}
          progress={null}
          runError={null}
        />
      )}

      <div className="flex justify-between mt-6">
        <Button size="large" onClick={onPrevious}>
          Previous
        </Button>
        <Button type="primary" size="large" onClick={onNext}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default StepPowerAnalysis;
